import { FC, memo, useCallback, useMemo } from 'react';
import { useController } from 'react-hook-form';

import FormControl from '@material-ui/core/FormControl';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';

import { Checkbox } from '../checkbox/Checkbox';
import { HelperTxt } from '../helperTxt/HelperTxt';
import { useFormCheckboxStyles } from './FormItems.styles';
import { FormCheckboxProps } from './FormItems.types';

export const FormCheckboxSkeleton: FC<Pick<FormCheckboxProps, 'required' | 'label'>> = memo(({ required, label }) => {
  const styles = useFormCheckboxStyles();

  return (
    <FormControl className={styles.formControl}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Skeleton variant="rect" width={24} height={24} style={{ borderRadius: 4 }} />
        {label && <Skeleton variant="text" width={120} height={20} />}
      </div>
      {required && <Skeleton variant="text" width={80} height={24} style={{ marginTop: 4 }} />}
    </FormControl>
  );
});

export const FormCheckbox: FC<FormCheckboxProps> = memo(
  ({ control, name, size = 'medium', variant = 'outline', required, loading = false, label, ...rest }) => {
    const styles = useFormCheckboxStyles();

    const {
      field: { value, ...field },
      fieldState: { error },
    } = useController({
      name,
      control,
    });

    const helperText = useMemo(() => {
      if (error) {
        return error.message;
      }

      if (required) {
        return '*required';
      }

      return '';
    }, [error, required]);

    const handleChange = useCallback(
      (checked: boolean) => {
        if (rest.onChange) {
          rest.onChange(checked);

          return;
        }

        field.onChange(checked);
      },
      [field, rest],
    );

    // Show skeleton when loading
    if (loading) {
      return <FormCheckboxSkeleton required={required} label={label} />;
    }

    return (
      <FormControl error={!!error} className={styles.formControl}>
        <Checkbox
          {...rest}
          {...field}
          className={clsx(rest.className, 'checkbox width-fit')}
          checked={!!value}
          size={size}
          variant={variant}
          required={required}
          label={label}
          onChange={handleChange}
        />

        {helperText && <HelperTxt>{helperText}</HelperTxt>}
      </FormControl>
    );
  },
);
